import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Box, Typography, Alert, Stack, CircularProgress, Chip,
    Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import api from '../../services/api';
import ServerPickerInline from './ServerPickerInline';

const STATUS_CHIP = {
    ok:        { label: 'OK',       color: 'success' },
    nxdomain:  { label: 'Sem PTR',  color: 'default' },
    noanswer:  { label: 'Sem resposta', color: 'default' },
    timeout:   { label: 'Timeout',  color: 'warning' },
    error:     { label: 'Erro',     color: 'error' },
};

/**
 * Reverse DNS — IP → hostname (PTR record). Consulta nos nós ou DNS público.
 */
export default function DnsReverseLookup({ open, onClose, inline = false }) {
    const [ip, setIp] = useState('');
    const [picker, setPicker] = useState({ node_ids: [], public_ids: [], custom_servers: [] });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reset = () => {
        setIp('');
        setResult(null);
        setError(null);
        setLoading(false);
    };
    const handleClose = () => {
        if (loading) return;
        reset();
        onClose && onClose();
    };

    const run = async () => {
        const v = ip.trim();
        if (!v) {
            setError('Informe um IP');
            return;
        }
        const totalSel = (picker.node_ids?.length || 0) + (picker.public_ids?.length || 0) + (picker.custom_servers?.length || 0);
        if (totalSel === 0) {
            setError('Selecione pelo menos 1 servidor DNS');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await api.post('/dns/node/reverse', {
                ip: v,
                node_ids: picker.node_ids,
                public_ids: picker.public_ids,
                custom_servers: picker.custom_servers,
            });
            if (res.data?.error) {
                setError(res.data.error);
            } else {
                setResult(res.data);
            }
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    const body = (
        <>
                {!inline && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Consulta o hostname associado a um IP (PTR record). Aceita IPv4
                        e IPv6 — converte automaticamente pro formato in-addr.arpa /
                        ip6.arpa.
                    </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth size="small" label="IP"
                        placeholder="ex.: 8.8.8.8 ou 2001:4860:4860::8888"
                        value={ip}
                        onChange={(e) => setIp(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } }}
                        disabled={loading}
                        autoFocus
                    />
                    <Button
                        variant="contained" color="primary" onClick={run}
                        disabled={loading || !ip.trim()} sx={{ minWidth: 110 }}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Consultar'}
                    </Button>
                </Stack>

                <ServerPickerInline value={picker} onChange={setPicker} disabled={loading} defaultPublic={false} />

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {result && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>{result.ip}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', mb: 2 }}>
                            {result.reverse_name}
                        </Typography>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Servidor</TableCell>
                                    <TableCell sx={{ width: 110 }}>Status</TableCell>
                                    <TableCell>Hostname (PTR)</TableCell>
                                    <TableCell align="right" sx={{ width: 70 }}>Tempo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.servers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                                            Nenhum servidor disponível
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    result.servers.map((s, i) => {
                                        const chip = STATUS_CHIP[s.status] || STATUS_CHIP.error;
                                        return (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    {s.name}
                                                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                                                        {s.server}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                    {s.records && s.records.length > 0 ? (
                                                        s.records.map((r, j) => <div key={j}>{r}</div>)
                                                    ) : (
                                                        <span style={{ color: '#999' }}>{s.error || s.rcode || '—'}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: 12 }}>
                                                    {s.time_ms}ms
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}
        </>
    );

    if (inline) return <Box>{body}</Box>;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Reverse DNS (PTR)</DialogTitle>
            <DialogContent dividers>{body}</DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
